import React, { useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./Interior.messages";
import styles from "./Interior.module.scss";

import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import { useUi } from "@jelly/lesson";
import { AccordionContent1 } from "./content/AccordionContent1";
import { AccordionContent2 } from "./content/AccordionContent2";
import { nl2p } from "@src/utils/nl2p";

const ACCORDION_IDS = {
  accordion1: "accordion1",
  accordion2: "accordion2",
} as const;

type InteriorSlide = "accordion1" | "accordion2";

interface InteriorContextData {
  setActiveSlide: (slide: InteriorSlide) => void;
  activeSlide: InteriorSlide;
}

const InteriorContext = React.createContext<InteriorContextData>({
  activeSlide: "accordion1",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

interface InteriorProps {
  title: string;
}

export const Interior: React.FC<InteriorProps> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<InteriorSlide>("accordion1");

  return (
    <InteriorContext.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <InteriorContent title={title} />
    </InteriorContext.Provider>
  );
};

const InteriorContent: React.FC<InteriorProps> = ({ title }) => {
  const ui = useUi();
  const { formatMessage } = useIntl();
  const { activeSlide } = useContext(InteriorContext);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem
          id={ACCORDION_IDS.accordion1}
          title={formatMessage(messages.C8_P3_S1_A1_Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.C8_P3_S1_A1_Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.accordion2}
          title={formatMessage(messages.C8_P3_S1_A2_Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.C8_P3_S1_A2_Description))}</p>
          <strong>{formatMessage(messages.C8_P3_S1_A2_P1_Title)}</strong>
          <p>{nl2p(formatMessage(messages.C8_P3_S1_A2_P1_Description))}</p>

          <strong>{formatMessage(messages.C8_P3_S1_A2_P2_Title)}</strong>
          <p>{nl2p(formatMessage(messages.C8_P3_S1_A2_P2_Description))}</p>

          <strong>{formatMessage(messages.C8_P3_S1_A2_P3_Title)}</strong>
          <p>{nl2p(formatMessage(messages.C8_P3_S1_A2_P3_Description))}</p>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "accordion1":
      content = <AccordionContent1 />;
      break;
    case "accordion2":
      content = <AccordionContent2 />;
      break;
  }

  return (
    <div className={styles.root}>
      <StandardCase
        title={title}
        leftComponent={
          <Accordion allowMultiOpen={false}>
            <MainAccordionObserver />
            {accordion}
          </Accordion>
        }
      >
        <div className={styles.content}>{content}</div>
      </StandardCase>
    </div>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(InteriorContext);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: InteriorSlide = "accordion1";
      switch (openItem) {
        case ACCORDION_IDS.accordion1:
          slide = "accordion1";
          break;
        case ACCORDION_IDS.accordion2:
          slide = "accordion2";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
